import {Loader, Application, Sprite, Texture, Graphics, Container, Filter } from 'pixi.js';
import { debounce } from "../../../utils";
class BioFutureHero {
    constructor(wrapper) {
        this.wrapper = wrapper;

        this.makeWrapperRectangle();
        this.createCanvas();
        this.binds();
        this.initEvents();
    }

    binds() {
        this.resizeHandler = this.resizeHandler.bind(this);
        this.resize = this.resize.bind(this);
    }

    initEvents() {
        window.addEventListener('resize', debounce(this.resize, 50));
    }

    loadImages() {
        const loader = new Loader();
        loader.add('maskImage', '/wp-content/themes/a16z/assets/images/bh/hero-topo-pattern-bio-future.svg');
        loader.load((loader, resources) => {
            this.onImagesLoaded(resources);
        });
    }

    onImagesLoaded(resources) {
        this.maskSprite = new Sprite(resources.maskImage.texture);
        this.maskSprite.originalWidth = this.maskSprite.width;
        this.maskSprite.originalHeight = this.maskSprite.height;
        this.applyObjectFitRightHeight(this.maskSprite);
        this.app.stage.addChild(this.maskSprite);
        this.createBackground();

        this.app.ticker.add((delta) => {
            this.backgroundFilter.uniforms.iTime += delta / 60;
            this.backgroundContainer.rotation += 0.0005;
        });
    }

    applyObjectFitRightHeight(sprite) {
        const scale = this.elHeight / sprite.originalHeight;
        sprite.scale.set(scale);
        sprite.x = this.elWidth - sprite.width;
        sprite.y = 0;
    }

    resize() {
        const prevWidth = this.elWidth;
        this.elWidth = this.wrapper.offsetWidth;

        if(prevWidth !== this.elWidth) {
            this.resizeHandler();
        }
    }

    createCanvas() {
        this.app = new Application({
            width: this.elWidth,
            height: this.elHeight,
            backgroundColor: 0x000000,
            backgroundAlpha: 0,
            resolution: 2,
            autoResize: true,
            antialias: true,
        });

        this.wrapper.appendChild(this.app.view);
        this.app.renderer.resize(this.elWidth, this.elHeight);

        this.createScene();
    }

    createScene() {
        this.scene = new Container();
        this.scene.width = this.elWidth;
        this.scene.height = this.elHeight;
        this.app.stage.addChild(this.scene);
        this.loadImages();
    }

    createCrossMask() {
        this.crossMask = new Graphics();
        this.crossMask.beginFill(0xffffff);
        this.crossMask.drawRect(0, this.elHeight * .3, this.elWidth, this.elHeight * .4);
        this.crossMask.drawRect(this.elWidth * .43, 0, this.elWidth * .43, this.elHeight);
        this.crossMask.endFill();
        this.scene.addChild(this.crossMask);
        this.backgroundWrapper.mask = this.crossMask;
    }

    createBackground() {
        this.backgroundWrapper = new Container();
        this.backgroundContainer = new Container();
        this.backgroundContainer.width = this.elWidth;
        this.backgroundContainer.height = this.elHeight;
        this.backgroundContainer.position.set(this.elWidth / 2, this.elHeight / 2);
        this.backgroundContainer.pivot.set(this.elWidth / 2, this.elHeight / 2);

        this.background = new Graphics();
        this.background.beginFill(0xff0000);
        this.background.drawRect(0, 0, this.elWidth, this.elHeight);
        this.background.endFill();

        this.shaderFrag = this.createFragmentShaderCode();

        const colorHex = [
            "#a77ad7",
            "#00a5b1",
            "#5bc3f5",
            "#00a5b1",
        ]

        this.colorFirst = this.hexToNormalizedRGB(colorHex[0]);
        this.colorSecond = this.hexToNormalizedRGB(colorHex[1]);
        this.colorFourth = this.hexToNormalizedRGB(colorHex[2]);
        this.colorThird = this.hexToNormalizedRGB(colorHex[3]);

        this.uniforms = {
            iTime: 0,
            iResolution: [this.elWidth, this.elHeight],
            u_colorFirst: [this.colorFirst.r, this.colorFirst.g, this.colorFirst.b],
            u_colorSecond: [this.colorSecond.r, this.colorSecond.g, this.colorSecond.b],
            u_colorThird: [this.colorThird.r, this.colorThird.g, this.colorThird.b],
            u_colorFourth: [this.colorFourth.r, this.colorFourth.g, this.colorFourth.b],
        }

        this.background.position.set(0, 0)
        this.backgroundFilter = new Filter(null, this.shaderFrag, this.uniforms);
        this.background.filters = [this.backgroundFilter];

        this.backgroundContainer.mask = this.maskSprite;

        this.backgroundContainer.addChild(this.background);
        this.backgroundWrapper.addChild(this.backgroundContainer);
        this.createCrossMask();
        this.scene.addChild(this.backgroundWrapper);
        this.createSpotCircles();
    }

    createSpotCircles() {
        this.spotCircles = [];
        this.spotCirclesContainer = new Container();
        const spotCircleCount = 3;
        const spotCircleRadiusMin = .3; // 10% of the scene width
        const spotCircleRadiusMax = .5; // 25% of the scene width
        const movementSpeedMin = .005;
        const movementSpeedMax = .007;
        const movementPath = [
            'horizontalOval',
            'verticalOval',
            'lemniscateBernulli',
            'petalFlower',
        ]


        for (let i = 0; i < spotCircleCount; i++) {
            const container = new Container();
            const spotCircleRadiusRandom = Math.random() * (spotCircleRadiusMax - spotCircleRadiusMin) + spotCircleRadiusMin;
            const canvasSize = this.elWidth * spotCircleRadiusRandom * 2;  // assuming the circle's diameter

            // Create radial gradient in an off-screen HTML Canvas
            const offscreenCanvas = document.createElement("canvas");
            offscreenCanvas.width = canvasSize;
            offscreenCanvas.height = canvasSize;
            const ctx = offscreenCanvas.getContext("2d");
            const spotCircleGradient = ctx.createRadialGradient(
                canvasSize / 2, canvasSize / 2, 0,
                canvasSize / 2, canvasSize / 2, canvasSize / 2
            );

            const firstColorSize = Math.random() * (0.45 - 0.15) + 0.15;
            spotCircleGradient.addColorStop(0, '#A77AD7');
            spotCircleGradient.addColorStop(firstColorSize, '#A77AD7');
            spotCircleGradient.addColorStop(1, '#00A4B1');

            ctx.fillStyle = spotCircleGradient;
            ctx.arc(canvasSize / 2, canvasSize / 2, canvasSize / 2, 0, 2 * Math.PI);
            ctx.fill();

            // Create alpha mask for smooth edges
            const maskCanvas = document.createElement("canvas");
            maskCanvas.width = canvasSize;
            maskCanvas.height = canvasSize;
            const maskCtx = maskCanvas.getContext("2d");
            const gradientMask = maskCtx.createRadialGradient(
                canvasSize / 2, canvasSize / 2, 0,
                canvasSize / 2, canvasSize / 2, canvasSize / 2
            );
            gradientMask.addColorStop(0, 'rgba(255, 255, 255, 1)');
            gradientMask.addColorStop(1, 'rgba(255, 255, 255, 0)');
            maskCtx.fillStyle = gradientMask;
            maskCtx.arc(canvasSize / 2, canvasSize / 2, canvasSize / 2, 0, 2 * Math.PI);
            maskCtx.fill();
            const maskTexture = Texture.from(maskCanvas);
            const maskSprite = new Sprite(maskTexture);
            maskSprite.anchor.set(0.5, 0.5);

            // Convert this canvas to a PIXI texture and apply to a PIXI sprite
            const gradientTexture = Texture.from(offscreenCanvas);
            const spotCircle = new Sprite(gradientTexture);

            spotCircle.mask = maskSprite; // Set the mask
            spotCircle.addChild(maskSprite);

            // Center anchor point (0.5, 0.5 makes it easier to manage)
            spotCircle.anchor.set(0.5, 0.5);

            spotCircle.initPosition = {
                x: Math.random() * this.elWidth,
                y: Math.random() * this.elHeight,
            }

            spotCircle.position.set(spotCircle.initPosition.x, spotCircle.initPosition.y);
            // spotCircle.alpha = .2;
            spotCircle.movementSpeed = Math.random() * (movementSpeedMax - movementSpeedMin) + movementSpeedMin;
            spotCircle.movementPath = movementPath[Math.floor(Math.random() * movementPath.length)];
            // spotCircle.
            // container.blendMode = BLEND_MODES.MULTIPLY;
            container.addChild(spotCircle);
            this.spotCircles.push(spotCircle);
            this.spotCirclesContainer.addChild(container);
            // this.spotCirclesContainer.blendMode = BLEND_MODES.LIGHTEN;
        }
        this.backgroundContainer.addChild(this.spotCirclesContainer);

        this.spotCirclesContainer.mask = this.maskSprite;

        let step = 0;
        this.app.ticker.add((delta) => {
            step += delta;
            this.spotCircles.forEach((spotCircle, index) => {
                let a = this.elWidth * 0.5; // half width of the movement ellipse
                let b = this.elHeight * 0.5; // half height of the movement ellipse
                switch (spotCircle.movementPath) {
                    case 'horizontalOval':
                        spotCircle.position.x = a * Math.cos(spotCircle.movementSpeed * step) + spotCircle.initPosition.x;
                        spotCircle.position.y = b * Math.sin(spotCircle.movementSpeed * step) + spotCircle.initPosition.y;
                        break;
                    case 'verticalOval':
                        spotCircle.position.x = a * Math.sin(spotCircle.movementSpeed * step) + spotCircle.initPosition.x;
                        spotCircle.position.y = b * Math.cos(spotCircle.movementSpeed * step) + spotCircle.initPosition.y;
                        break;
                    case 'lemniscateBernulli':
                        spotCircle.position.x = a * Math.cos(spotCircle.movementSpeed * step) / (1 + Math.sin(spotCircle.movementSpeed * step) ** 2) + spotCircle.initPosition.x;
                        spotCircle.position.y = a * Math.cos(spotCircle.movementSpeed * step) * Math.sin(spotCircle.movementSpeed * step) / (1 + Math.sin(spotCircle.movementSpeed * step) ** 2) + spotCircle.initPosition.y;
                        break;
                    case 'petalFlower':
                        spotCircle.position.x = a * Math.cos(3 * spotCircle.movementSpeed * step) * Math.cos(spotCircle.movementSpeed * step) + spotCircle.initPosition.x;
                        spotCircle.position.y = a * Math.cos(3 * spotCircle.movementSpeed * step) * Math.sin(spotCircle.movementSpeed * step) + spotCircle.initPosition.y;
                        break;
                }
            });
        })
    }

    makeWrapperRectangle() {
        this.elHeight = this.wrapper.offsetHeight;
        this.elWidth = this.elHeight;
        this.wrapper.style.width = this.elWidth + "px";
    }

    resizeHandler() {
        this.makeWrapperRectangle();
        this.app.renderer.resize(this.elWidth, this.elHeight);
    }

    hexToNormalizedRGB(hex) {
        let bigint = parseInt(hex.substring(1), 16);
        let r = (bigint >> 16) & 255;
        let g = (bigint >> 8) & 255;
        let b = bigint & 255;

        return {
            r: r / 255.0,
            g: g / 255.0,
            b: b / 255.0
        };
    }

    createFragmentShaderCode() {
        return `
    precision mediump float;
    
    varying vec2 vTextureCoord;

    mat2 Rot(float a)
    {
        float s = sin(a);
        float c = cos(a);
        return mat2(c, -s, s, c);
    }

    vec2 hash(vec2 p)
    {
        p = vec2(dot(p, vec2(2127.1, 81.17)), dot(p, vec2(1269.5, 283.37)));
        return fract(sin(p) * 43758.5453);
    }

    float noise(in vec2 p)
    {
        vec2 i = floor(p);
        vec2 f = fract(p);

        vec2 u = f * f * (3.0 - 2.0 * f);

        float n = mix(mix(dot(-1.0 + 2.0 * hash(i + vec2(0.0, 0.0)), f - vec2(0.0, 0.0)),
                          dot(-1.0 + 2.0 * hash(i + vec2(1.0, 0.0)), f - vec2(1.0, 0.0)), u.x),
                      mix(dot(-1.0 + 2.0 * hash(i + vec2(0.0, 1.0)), f - vec2(0.0, 1.0)),
                          dot(-1.0 + 2.0 * hash(i + vec2(1.0, 1.0)), f - vec2(1.0, 1.0)), u.x), u.y);
        return 0.5 + 0.5 * n;
    }

    uniform vec2 iResolution;
    uniform float iTime;
    uniform vec3 u_colorFirst;
    uniform vec3 u_colorSecond;
    uniform vec3 u_colorThird;
    uniform vec3 u_colorFourth;

    void main()
    {
        vec2 uv = vTextureCoord;
        float ratio = iResolution.x / iResolution.y;
    
        vec2 tuv = uv;
        tuv -= .2;
    
        // rotate with Noise
        float degree = noise(vec2(iTime*.1, tuv.x*tuv.y));
    
        tuv.y *= 1./ratio;
        tuv *= Rot(radians((degree-.5)*620.+180.));
        tuv.y *= ratio;
    
        // Wave warp with sin
        float frequency = 4.;
        float amplitude = 40.;
        float speed = iTime * 2.;
        tuv.x += sin(tuv.y*frequency+speed)/amplitude;
        tuv.y += cos(tuv.x*frequency*1.5+speed)/(amplitude*.5);
        
        // draw the image
        vec3 colorFirst = u_colorFirst;
        vec3 colorSecond = u_colorSecond;
        vec3 layer1 = mix(colorFirst, colorSecond, smoothstep(-.3, .2, (tuv*Rot(radians(-5.))).x));
        
        vec3 colorThird = u_colorThird;
        vec3 colorFour = u_colorFourth;
        vec3 layer2 = mix(colorThird, colorFour, smoothstep(-.3, .2, (tuv*Rot(radians(-5.))).x));
        
        vec3 finalComp = mix(layer1, layer2, smoothstep(.5, -.3, tuv.y));
        
        vec3 col = finalComp;
        
        vec4 color = vec4(col, 1.0);
        
        gl_FragColor = color;
    }
    `;
    }
}

export default BioFutureHero
